<script>
    import { onMount } from "svelte";

    import { CardLayout } from "../../../Components/Common/index.svelte";

    import {
        checkProviderNumberValidation,
        importGoogleMapScript,
    } from "../../../Utils/utilFunctions.svelte";
    import Select from "svelte-select/Select.svelte";
    import { apiMethods } from "../../../Services/index.svelte";
    import { AustralianStates } from "../../../Utils/utilEnums.svelte";

    export let providerDetails = {
    };

    $: isValidProviderNumber = checkProviderNumberValidation(
        providerDetails.number,
    );

    const checkAndAutoPadProviderNumber = () => {
        if (
            providerDetails.number &&
            providerDetails.number.length < 8 &&
            !isValidProviderNumber
        ) {
            providerDetails.number = providerDetails.number.padStart(8, "0");
        }
    };

    let primaryLocationAddress,
        errInputs = [];

    onMount(() => importGoogleMapScript());

    export const checkProviderValidation = () => {
        errInputs = [];
        providerDetails = { ...providerDetails, isDefault: true };
        _updateInvalidFieldsStyle();

        return errInputs.length == [];
    };

    const loadLocationAddress = async (searchText) => {
        if (!searchText) return [];

        let res = await apiMethods.getMethod.getAddressFromGoogle(searchText);

        if (res && res.predictions) return [...res.predictions];
    };

    const clearSelectedPrimaryLocation = () => {
        primaryLocationAddress = null;

        _resetDefaultStyle("primaryLocationInput");
    };

    const handleSelectPrimaryLocation = async () => {
        await apiMethods.getMethod
            .getPlaceDetailsFromGoogle(primaryLocationAddress.place_id)
            .then((res) => {
                _syncLocationDetails(res);

                _resetDefaultStyle("primaryLocationAddressInput");
            });
    };

    // HELPER FUNCTIONS
    const _updateInvalidFieldsStyle = () => {
        if (!providerDetails.number || !isValidProviderNumber)
            _setErrorStyle("providerNumberInput");
        else _resetDefaultStyle("providerNumberInput");

        if (!primaryLocationAddress?.place_id)
            _setErrorStyle("primaryLocationInput");
        else _resetDefaultStyle("primaryLocationInput");

        if (!providerDetails.locationAddress)
            _setErrorStyle("primaryLocationAddressInput");
        else _resetDefaultStyle("primaryLocationAddressInput");

        if (errInputs.length) {
            const scrollToElement = document.getElementById(errInputs[0]);
            if (scrollToElement)
                scrollToElement.scrollIntoView({ behavior: "smooth" });
        }
    };

    const _syncLocationDetails = (details) => {
        providerDetails.location = primaryLocationAddress.description;

        providerDetails.locationAddress = details.formatted_address;

        if (details.address_components)
            for (let component of details.address_components) {
                let types = component["types"];

                if (types.includes("locality"))
                    providerDetails.city = component["long_name"];
                else if (types.includes("country"))
                    providerDetails.country = component["long_name"];
                else if (types.includes("postal_code"))
                    providerDetails.postCode = component["long_name"];
                else if (types.includes("street_number"))
                    providerDetails.streetNumber = component["long_name"];
                else if (types.includes("route"))
                    providerDetails.streetName = component["long_name"];
                else if (types.includes("administrative_area_level_1"))
                    // use short name to match backend requests and sync with local constants
                    providerDetails.state = component["short_name"];
            }
    };

    const _setErrorStyle = (id) => {
        errInputs.push(id);
        const field = document.getElementById(`${id}`);
        field.style.outline = "red solid 2px";
    };

    const _resetDefaultStyle = (id) => {
        const field = document.getElementById(`${id}`);
        field.style.outline = "none";
    };
</script>

<CardLayout title="Provider Details">
    <div class="flex flex-col gap-y-4 p-4">
        <div class="flex items-start gap-x-4">
            <p class="title mandatory">Provider Number</p>
            <div class="flex flex-col flex-1 gap-y-2">
                <input
                    type="text"
                    class="input input-sm input-bordered"
                    id="providerNumberInput"
                    autocomplete="off"
                    bind:value={providerDetails.number}
                    on:focus={() => _resetDefaultStyle("providerNumberInput")}
                    on:blur={checkAndAutoPadProviderNumber}
                />

                {#if !isValidProviderNumber}
                    <p class="text-error">
                        Please provide a valid provider number
                    </p>
                {/if}
            </div>
        </div>

        <div class="flex items-center gap-x-4">
            <input
                type="checkbox"
                class="toggle toggle-secondary"
                bind:checked={providerDetails.isPortingAcross}
            />
            <p>
                This provider number has previously been registered with health
                fund.
            </p>
        </div>

        <div class="flex items-center gap-x-4">
            <p class="title mandatory">Location</p>

            <div class="flex flex-1" id="primaryLocationInput">
                <Select
                    class="rounded-full"
                    bind:value={primaryLocationAddress}
                    loadOptions={loadLocationAddress}
                    itemId="place_id"
                    label="description"
                    on:clear={clearSelectedPrimaryLocation}
                    on:select={handleSelectPrimaryLocation}
                    noOptionsMessage="No available locations found"
                    placeholder="Type and select location"
                />
            </div>
        </div>

        <div class="flex items-center gap-x-4">
            <p class="title mandatory">Primary Location Address</p>
            <input
                type="text"
                class="input input-sm input-bordered flex-1"
                id="primaryLocationAddressInput"
                bind:value={providerDetails.locationAddress}
                on:focus={() =>
                    _resetDefaultStyle("primaryLocationAddressInput")}
            />
        </div>

        <div class="flex items-center gap-x-4">
            <p class="title">Secondary Location Address</p>
            <input
                type="text"
                class="input input-sm input-bordered flex-1"
                id="secondaryLocationAddressInput"
                bind:value={providerDetails.locationAddress2}
            />
        </div>

        <div class="flex flex-1 gap-x-4">
            <div class="flex flex-col flex-1 gap-y-4">
                <div class="flex items-center gap-x-4">
                    <p class="shortTitle">City</p>
                    <input
                        type="text"
                        class="input input-sm input-bordered"
                        id="cityInput"
                        bind:value={providerDetails.city}
                    />
                </div>

                <div class="flex items-center gap-x-4">
                    <p class="shortTitle mandatory">State</p>
                    <select
                        class="input input-sm input-bordered w-full"
                        bind:value={providerDetails.state}
                    >
                        {#each AustralianStates as state}
                            <option value={state.key}>
                                {state.name}
                            </option>
                        {/each}
                    </select>
                </div>

                <div class="flex items-center gap-x-4">
                    <p class="shortTitle">Postcode</p>
                    <input
                        type="text"
                        class="input input-sm input-bordered"
                        id="postcodeInput"
                        bind:value={providerDetails.postCode}
                    />
                </div>
            </div>

            <div class="divider divider-horizontal" />

            <div class="flex flex-col flex-1 gap-y-4">
                <div class="flex items-center gap-x-4">
                    <p class="shortTitle">BUPA</p>
                    <div class="btn-group btn-group-horizontal">
                        <button
                            on:click|preventDefault={() =>
                                (providerDetails.isNoGapBupa = true)}
                            class="btn btn-sm normal-case {providerDetails.isNoGapBupa
                                ? 'btn-secondary text-white'
                                : ''}">No Gap</button
                        >
                        <button
                            on:click|preventDefault={() =>
                                (providerDetails.isNoGapBupa = false)}
                            class="btn btn-sm normal-case {providerDetails.isNoGapBupa
                                ? ''
                                : 'btn-secondary text-white'}">Known Gap</button
                        >
                    </div>
                </div>

                <div class="flex items-center gap-x-4">
                    <p class="shortTitle">HCF</p>
                    <div class="btn-group btn-group-horizontal">
                        <button
                            on:click|preventDefault={() =>
                                (providerDetails.isNoGapHCF = true)}
                            class="btn btn-sm normal-case {providerDetails.isNoGapHCF
                                ? 'btn-secondary text-white'
                                : ''}">No Gap</button
                        >
                        <button
                            on:click|preventDefault={() =>
                                (providerDetails.isNoGapHCF = false)}
                            class="btn btn-sm normal-case {providerDetails.isNoGapHCF
                                ? ''
                                : 'btn-secondary text-white'}">Known Gap</button
                        >
                    </div>
                </div>

                {#if providerDetails.state && providerDetails.state == "WA"}
                    <div class="flex items-center gap-x-4">
                        <p class="shortTitle">HCF WA Cover Type</p>
                        <div class="btn-group btn-group-horizontal">
                            <button
                                on:click|preventDefault={() =>
                                    (providerDetails.isFullyCoveredHBFWA = 0)}
                                class="btn btn-sm normal-case {providerDetails.isFullyCoveredHBFWA == 0
                                    ? 'btn-secondary text-white'
                                    : ''}">Inactive</button
                            >
                            <button
                                on:click|preventDefault={() =>
                                    (providerDetails.isFullyCoveredHBFWA = 1)}
                                class="btn btn-sm normal-case {providerDetails.isFullyCoveredHBFWA == 1
                                    ? 'btn-secondary text-white'
                                    : ''}">Provider Choice</button
                            >
                            <button
                                on:click|preventDefault={() =>
                                    (providerDetails.isFullyCoveredHBFWA = 2)}
                                class="btn btn-sm normal-case {providerDetails.isFullyCoveredHBFWA == 2
                                    ? 'btn-secondary text-white'
                                    : ''}">Fully Covered</button
                            >
                        </div>
                    </div>
                {/if}

                <div class="flex items-center gap-x-4">
                    <p class="shortTitle">NIB</p>
                    <div class="btn-group btn-group-horizontal">
                        <button
                            on:click|preventDefault={() =>
                                (providerDetails.isNoGapNib = true)}
                            class="btn btn-sm normal-case {providerDetails.isNoGapNib
                                ? 'btn-secondary text-white'
                                : ''}">Medigap</button
                        >
                        <button
                            on:click|preventDefault={() =>
                                (providerDetails.isNoGapNib = false)}
                            class="btn btn-sm normal-case {providerDetails.isNoGapNib
                                ? ''
                                : 'btn-secondary text-white'}">GapSure</button
                        >
                    </div>
                </div>
            </div>
        </div>

        <div class="flex items-center gap-x-4">
            <input
                type="checkbox"
                class="toggle toggle-secondary"
                bind:checked={providerDetails.isPreferredDefence}
            />
            <p>
                Preferred provider for Australian Defence Force? (leave
                unchecked if unsure)
            </p>
        </div>
    </div>
</CardLayout>

<style>
    .mandatory:after {
        content: " * ";
    }

    .title {
        line-height: 1.5rem;
        font-size: 1rem;
        font-weight: 700;
        min-width: 14rem;
    }

    .shortTitle {
        line-height: 1.5rem;
        font-size: 1rem;
        font-weight: 700;
        min-width: 10rem;
    }
</style>
